import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import axios from 'axios';

const SetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-email`, { token });
      setUserEmail(response.data.user.email);
      setUserName(response.data.user.name);
      setVerifying(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid or expired verification link');
      setVerifying(false);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (): boolean => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const getPasswordStrength = (): { strength: string; color: string; width: string } => {
    if (password.length === 0) return { strength: '', color: '', width: '0%' };
    if (password.length < 8) return { strength: 'Too short', color: '#ff4d4f', width: '25%' };
    
    let score = 0;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score === 0) return { strength: 'Weak', color: '#ff4d4f', width: '33%' };
    if (score === 1) return { strength: 'Fair', color: '#faad14', width: '50%' };
    if (score === 2) return { strength: 'Good', color: '#52c41a', width: '75%' };
    return { strength: 'Strong', color: '#52c41a', width: '100%' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/set-password`, { token, password });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  const strengthInfo = getPasswordStrength();

  if (loading || verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <p style={styles.verifyingText}>Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !userEmail) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Invalid Link</h2>
          <p style={styles.errorMessage}>{error}</p>
          <p style={styles.helpText}>
            The verification link may have expired or is invalid. Please contact your manager for a new invitation.
          </p>
          <button onClick={() => navigate('/login')} style={styles.backButton}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>Password Set Successfully!</h2>
          <p style={styles.successMessage}>
            Your account is now active. Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <h1 style={styles.logoText}>ShahoSuite</h1>
        </div>
        
        <h2 style={styles.title}>Set Your Password</h2>
        <p style={styles.subtitle}>
          Welcome, <strong>{userName}</strong>! <br />
          Create a secure password for your account.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={userEmail}
              disabled
              style={{ ...styles.input, ...styles.inputDisabled }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              style={styles.input}
              required
            />
            {password && (
              <div style={styles.strengthContainer}>
                <div style={styles.strengthBar}>
                  <div style={{
                    ...styles.strengthBarFill,
                    width: strengthInfo.width,
                    backgroundColor: strengthInfo.color
                  }}></div>
                </div>
                <span style={{ ...styles.strengthText, color: strengthInfo.color }}>
                  {strengthInfo.strength}
                </span>
              </div>
            )}
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              style={styles.input}
              required
            />
          </div>

          {error && <div style={styles.errorBanner}>{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            style={submitting ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
          >
            {submitting ? 'Setting Password...' : 'Set Password & Activate Account'}
          </button>
        </form>

        <p style={styles.footer}>
          Need help? Contact <a href="mailto:support@shahosuite.com" style={styles.link}>support@shahosuite.com</a>
        </p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    maxWidth: '480px',
    width: '100%',
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  logoText: {
    fontSize: '28px',
    color: '#667eea',
    margin: 0,
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '10px',
    color: '#333',
  },
  subtitle: {
    textAlign: 'center',
    color: '#666',
    marginBottom: '30px',
    lineHeight: '1.6',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#333',
  },
  input: {
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #d9d9d9',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.3s',
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#999',
    cursor: 'not-allowed',
  },
  strengthContainer: {
    marginTop: '8px',
  },
  strengthBar: {
    height: '4px',
    width: '100%',
    backgroundColor: '#f0f0f0',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  strengthBarFill: {
    height: '100%',
    transition: 'width 0.3s, background-color 0.3s',
  },
  strengthText: {
    fontSize: '12px',
    marginTop: '4px',
    display: 'block',
  },
  button: {
    padding: '14px',
    fontSize: '16px',
    fontWeight: 'bold',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '10px',
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  errorBanner: {
    padding: '12px',
    backgroundColor: '#fff2f0',
    border: '1px solid #ffccc7',
    borderRadius: '6px',
    color: '#ff4d4f',
    fontSize: '14px',
  },
  footer: {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '14px',
    color: '#666',
  },
  link: {
    color: '#667eea',
    textDecoration: 'none',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  verifyingText: {
    textAlign: 'center',
    color: '#666',
  },
  errorIcon: {
    fontSize: '60px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  errorTitle: {
    textAlign: 'center',
    color: '#ff4d4f',
    marginBottom: '10px',
  },
  errorMessage: {
    textAlign: 'center',
    color: '#666',
    marginBottom: '20px',
  },
  helpText: {
    textAlign: 'center',
    color: '#999',
    fontSize: '14px',
    marginBottom: '20px',
  },
  backButton: {
    display: 'block',
    margin: '0 auto',
    padding: '12px 24px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  successIcon: {
    fontSize: '80px',
    textAlign: 'center',
    color: '#52c41a',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  successTitle: {
    textAlign: 'center',
    color: '#52c41a',
    marginBottom: '10px',
  },
  successMessage: {
    textAlign: 'center',
    color: '#666',
  },
};

export default SetPassword;
